// ---------------------------------------------------------------
// TagAlong Fleet — the commercial portal at /fleet.
//
// Consumer TagAlong answers "where is my car?" — you browse one vehicle at a
// time. Fleet answers a different question: "is anything wrong across all of
// them, and who was driving?" So the default view is a table sorted by what
// needs attention, not a map. Exceptions first, everything else quiet.
// ---------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { getFleet } from '../traccar';
import {
  createFleetAccount, fleetLogin, fleetLogout, getFleetSession,
  loadFleetData, saveFleetData, addDriver, removeDriver, assignDriver, driverFor,
  claimVehicle, releaseVehicle, loadLiveFleet, getTelematicsStatus, connectSamsara,
  loadTelematicsDrivers, loadVehiclesFull, discoverTelematics,
} from '../fleetStore';
import { PRODUCT } from '../product';
import FleetOverview from './FleetOverview';
import FleetRuns from './FleetRuns';

const BG = '#070b14';
const PANEL = '#0c1220';
const TXT = '#e8ebf5';
const MUTED = '#9aa0b4';
const HAIR = '1px solid rgba(255,255,255,0.09)';
const ACCENT = '#f97316';

const card = {
  background: PANEL, border: HAIR, borderRadius: '16px', padding: '18px',
};
const inp = {
  width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: '10px',
  border: HAIR, background: 'rgba(255,255,255,0.05)', color: TXT, fontSize: '14px',
  outline: 'none',
};
const btn = (primary) => ({
  padding: '11px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer',
  fontWeight: 700, fontSize: '14px',
  background: primary ? `linear-gradient(135deg, ${ACCENT}, #f4511e)` : 'rgba(255,255,255,0.08)',
  color: primary ? 'white' : TXT,
});

// ---------- sign in / sign up ----------
function FleetLogin({ onIn }) {
  const [mode, setMode] = useState('login');
  const [f, setF] = useState({ company: '', name: '', email: '', password: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const u = mode === 'login'
        ? await fleetLogin(f.email, f.password)
        : await createFleetAccount(f);
      onIn(u);
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: BG, color: TXT, position: 'relative' }}>
      {/* Background video — muted and looping. `playsInline` matters: without it
          iOS takes the video fullscreen instead of leaving it behind the form. */}
      <video
        autoPlay muted loop playsInline
        src="/fleet-login.mp4"
        style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
      />
      {/* Scrim: keeps the form readable no matter how bright the footage gets. */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 1, background: 'linear-gradient(180deg, rgba(5,7,15,.55), rgba(5,7,15,.30) 42%, rgba(5,7,15,.85))' }} />

      <div style={{ position: 'relative', zIndex: 2, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <form onSubmit={submit} style={{
        width: 'min(420px, 100%)', borderRadius: '18px', padding: '26px',
        // Glass: genuinely translucent so the video reads through it, with a
        // bright top edge and a soft drop shadow to lift it off the footage.
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.22)',
        boxShadow: '0 30px 80px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.16)',
        backdropFilter: 'blur(14px) saturate(130%)',
        WebkitBackdropFilter: 'blur(14px) saturate(130%)',
      }}>
        <div style={{ fontSize: '24px', fontWeight: 800, marginBottom: '4px' }}>{PRODUCT.name}</div>
        <div style={{ color: MUTED, fontSize: '13px', marginBottom: '20px' }}>{PRODUCT.tagline}</div>

        {mode === 'signup' && (
          <>
            <label style={{ fontSize: '12px', color: MUTED, fontWeight: 600 }}>Company</label>
            <input style={{ ...inp, margin: '5px 0 12px' }} value={f.company} onChange={set('company')} placeholder="Acme Logistics LLC" />
            <label style={{ fontSize: '12px', color: MUTED, fontWeight: 600 }}>Your name</label>
            <input style={{ ...inp, margin: '5px 0 12px' }} value={f.name} onChange={set('name')} placeholder="Jane Smith" />
          </>
        )}

        <label style={{ fontSize: '12px', color: MUTED, fontWeight: 600 }}>Work email</label>
        <input style={{ ...inp, margin: '5px 0 12px' }} type="email" autoComplete="username" value={f.email} onChange={set('email')} />

        <label style={{ fontSize: '12px', color: MUTED, fontWeight: 600 }}>Password</label>
        <input style={{ ...inp, margin: '5px 0 16px' }} type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={f.password} onChange={set('password')} />

        {err && (
          <div style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.32)', color: '#fca5a5', borderRadius: '10px', padding: '10px 12px', fontSize: '13px', marginBottom: '12px' }}>
            {err}
          </div>
        )}

        <button type="submit" disabled={busy} style={{ ...btn(true), width: '100%', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create company account'}
        </button>

        <div style={{ textAlign: 'center', marginTop: '14px', fontSize: '13px', color: MUTED }}>
          {mode === 'login' ? "Don't have an account? " : 'Already have one? '}
          <button type="button" onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setErr(''); }}
            style={{ background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>
            {mode === 'login' ? 'Create one' : 'Sign in'}
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '18px', fontSize: '12px', color: MUTED }}>
          Questions? {PRODUCT.supportPhoneDisplay}
        </div>
      </form>
      </div>
    </div>
  );
}

// ---------- exception ranking ----------
// What the fleet manager should look at first. A vehicle is only "fine" if
// nothing is flagged — everything else sorts to the top with a reason.
function exceptionOf(v) {
  // When the live classifier has spoken, trust it — it already knows the
  // difference between "parked at our own yard" and "stopped somewhere odd".
  if (v.state === 'dormant') return { level: 0, label: 'Out of service', tone: '#6b7280' };
  if (v.state === 'at-terminal') return { level: 0, label: `At ${v.terminal || 'terminal'}`, tone: MUTED };
  if (v.state === 'at-shop') return { level: 1, label: `At ${v.shop || 'shop'}`, tone: '#60a5fa' };
  if (v.state === 'idling') return { level: 2, label: 'Idling', tone: '#f59e0b' };
  if (v.state === 'at-customer') return { level: 1, label: `At ${v.customer || 'customer'}`, tone: '#22c55e' };
  if (v.state === 'moving') return { level: 1, label: `${v.speedMph} mph`, tone: '#22c55e' };
  if (v.state === 'parked') return { level: 1, label: 'Parked', tone: MUTED };
  if (v.stale) return { level: 3, label: 'No signal', tone: '#f59e0b' };
  if ((v.dtcCodes || []).length) return { level: 3, label: `Check engine (${v.dtcCodes.length})`, tone: '#ef4444' };
  if (v.batteryLevel != null && v.batteryLevel < 25) return { level: 2, label: `Battery ${Math.round(v.batteryLevel)}%`, tone: '#f59e0b' };
  if (v.speedMph > 80) return { level: 3, label: `${v.speedMph} mph`, tone: '#ef4444' };
  if (v.ignition) return { level: 1, label: v.speedMph > 0 ? `Moving ${v.speedMph} mph` : 'Idling', tone: '#22c55e' };
  return { level: 0, label: 'Parked', tone: MUTED };
}

// ---------- dashboard ----------
function FleetDashboard({ me, onOut }) {
  const [tab, setTab] = useState('overview');
  const [vehicles, setVehicles] = useState([]);
  const [data, setData] = useState({ drivers: [], assignments: {}, staff: [] });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const [live, setLive] = useState(null);        // classified telematics fleet
  const [tele, setTele] = useState({ connected: false });
  const [showDormant, setShowDormant] = useState(false);
  const [tmDrivers, setTmDrivers] = useState(null); // roster + assignments from Samsara
  const [full, setFull] = useState(null);           // per-vehicle telemetry
  const [probe, setProbe] = useState(null);         // what this account exposes

  const refresh = useCallback(async () => {
    try {
      const [d, status] = await Promise.all([loadFleetData(), getTelematicsStatus()]);
      setData(d); setTele(status);
      // Prefer the company's own telematics — that's where their trucks
      // actually are. Fall back to TagAlong devices if nothing is connected.
      try {
        const l = await loadLiveFleet({ includeDormant: showDormant });
        setLive(l);
        // Roster is best-effort: the vehicle list still works without it.
        loadTelematicsDrivers().then(setTmDrivers).catch(() => setTmDrivers(null));
        loadVehiclesFull().then(setFull).catch(() => setFull(null));
        setVehicles((l.rows || []).map((r) => ({
          id: r.id, name: r.name, speedMph: r.mph || 0,
          ignition: r.state === 'moving' || r.state === 'idling',
          stale: r.state === 'dormant', state: r.state,
          place: r.place || '', terminal: r.terminal, shop: r.shop, customer: r.customer,
          batteryLevel: null, dtcCodes: [],
        })));
      } catch {
        const v = await getFleet();
        setLive(null);
        setVehicles(v || []);
      }
      setErr('');
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [showDormant]);

  useEffect(() => {
    refresh();
    // Re-run the SAME loader the initial load used. This previously called
    // getFleet() directly, which meant that 30 seconds after connecting
    // Samsara the live vehicles were silently overwritten with Traccar's
    // (empty) list — counts stayed populated, the table went blank.
    const t = setInterval(() => { refresh(); }, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const persist = async (next) => {
    setData(next); // optimistic — the table shouldn't stutter while saving
    try { await saveFleetData(next); } catch (e) { setErr(e.message); refresh(); }
  };

  const ranked = [...vehicles].sort((a, b) => {
    const ea = exceptionOf(a), eb = exceptionOf(b);
    if (eb.level !== ea.level) return eb.level - ea.level;
    return String(a.name).localeCompare(String(b.name));
  });

  const needsAttention = ranked.filter((v) => exceptionOf(v).level >= 2).length;
  const moving = vehicles.filter((v) => v.ignition && v.speedMph > 0).length;

  const Stat = ({ label, value, tone }) => (
    <div style={{ ...card, flex: 1, minWidth: '130px', padding: '14px 16px' }}>
      <div style={{ fontSize: '11px', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: '26px', fontWeight: 800, color: tone || TXT, marginTop: '2px' }}>{value}</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: BG, color: TXT, padding: '18px' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: '20px', fontWeight: 800 }}>{me.company || PRODUCT.name}</div>
            <div style={{ fontSize: '12px', color: MUTED }}>{me.email}</div>
          </div>
          {live && live.counts.dormant > 0 && (
            <button onClick={() => setShowDormant(!showDormant)} style={btn(false)} title="Units with no report for over 14 days">
              {showDormant ? 'Hide' : 'Show'} {live.counts.dormant} dormant
            </button>
          )}
          <button onClick={refresh} style={btn(false)}>↻ Refresh</button>
          <button onClick={onOut} style={btn(false)}>Sign out</button>
        </div>

        {tab !== 'overview' && <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' }}>
          <Stat label="Vehicles" value={live ? live.counts.total : vehicles.length} />
          <Stat label="Moving now" value={live ? live.counts.moving : moving} tone="#22c55e" />
          <Stat label="Idling" value={live ? live.counts.idling : 0} tone={live && live.counts.idling ? '#f59e0b' : TXT} />
          <Stat label="At terminal" value={live ? live.counts.atTerminal : 0} />
          <Stat label="Dormant" value={live ? live.counts.dormant : 0} tone={live && live.counts.dormant ? '#9aa0b4' : TXT} />
          <Stat label="Drivers" value={data.drivers.length} />
        </div>}

        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          {['overview', 'runs', 'vehicles', 'drivers', 'data'].map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              ...btn(tab === t), textTransform: 'capitalize', padding: '9px 16px',
            }}>{t}</button>
          ))}
        </div>

        {err && (
          <div style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.32)', color: '#fca5a5', borderRadius: '10px', padding: '11px 13px', fontSize: '13px', marginBottom: '14px' }}>
            {err}
          </div>
        )}

        {loading && <div style={{ color: MUTED, fontSize: '14px' }}>Loading fleet…</div>}

        {tab === 'overview' && <FleetOverview company={me.company} />}
        {tab === 'runs' && <FleetRuns />}
        {tab === 'data' && <DataAudit probe={probe} onProbe={() => discoverTelematics().then(setProbe).catch((e) => setErr(e.message))} />}

        {!loading && tab === 'vehicles' && <ConnectTelematics status={tele} onConnected={refresh} />}
        {!loading && tab === 'vehicles' && !tele.connected && <AddVehicle onAdded={refresh} />}
        {!loading && tab === 'vehicles' && full && (
          <div style={{ ...card, marginBottom: '14px', display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
            {[
              // "Reporting" leads, because every other number below is counted
              // over reporting trucks only. Without it the fault count looks
              // like it covers the whole roster when it doesn't.
              ['Reporting', full.counts.reporting, '#22c55e'],
              ['Fault codes', full.counts.withFaults, '#ef4444'],
              ['Low fuel (<20%)', full.counts.lowFuel, '#f59e0b'],
              ['Low DEF (<20%)', full.counts.lowDef, '#f59e0b'],
              ['Low battery', full.counts.lowBattery, '#f59e0b'],
            ].map(([label, n, tone]) => (
              <div key={label}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: n ? tone : TXT }}>{n}</div>
                <div style={{ fontSize: '10px', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
              </div>
            ))}
            <div style={{ flex: 1, minWidth: '160px', fontSize: '11px', color: MUTED, alignSelf: 'center', lineHeight: 1.5 }}>
              {/* Engine hours is NOT in this list any more — Samsara has no
                  valid stat type for it, so promising it in the copy was a
                  small lie the UI kept telling. */}
              Live from the vehicle gateway — fuel, DEF, odometer, coolant, battery, air temp and fault codes.
              {full.counts.stale > 0 && (
                <>
                  {' '}
                  <b style={{ color: TXT }}>{full.counts.stale} not reporting</b> — a truck carrying a load
                  always has a live gateway, so these are almost certainly out of the fleet.
                </>
              )}
            </div>
          </div>
        )}

        {!loading && tab === 'vehicles' && (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {ranked.length === 0 && (
              <div style={{ padding: '28px', textAlign: 'center', color: MUTED, fontSize: '14px', lineHeight: 1.6 }}>
                No vehicles on this fleet yet.<br />
                <span style={{ fontSize: '13px' }}>Add one with the code from the vehicle&apos;s owner.</span>
              </div>
            )}
            {ranked.map((v, i) => {
              const ex = exceptionOf(v);
              const drv = driverFor(data, v.id);
              return (
                <div key={v.id} style={{
                  display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 16px',
                  borderTop: i ? HAIR : 'none', flexWrap: 'wrap',
                }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: ex.tone, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '14px' }}>{v.name}</span>
                      {/* Owner-operator vs company truck. A fault on someone
                          else's equipment escalates to a small business owner,
                          not the shop — so the dispatcher needs to see it
                          before deciding who to call. */}
                      {(() => {
                        const f = full && (full.vehicles || []).find((x) => String(x.id) === String(v.id));
                        const o = f && f.ownership;
                        if (!o || o.class !== 'owner-op') return null;
                        return (
                          <span title={`Owner-operator — ${o.owner}${o.fleetmateCount > 1 ? ` · ${o.fleetmateCount} trucks` : ''}`}
                            style={{
                              fontSize: '9px', fontWeight: 800, letterSpacing: '.06em',
                              padding: '1px 6px', borderRadius: '999px',
                              background: 'rgba(139,92,246,0.18)', color: '#c4b5fd',
                              border: '1px solid rgba(139,92,246,0.35)',
                            }}>O/O · {o.owner}</span>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize: '12px', color: ex.tone }}>
                      {ex.label}{v.place ? <span style={{ color: MUTED }}> · {v.place}</span> : null}
                    </div>
                    {/* Everything the gateway reports, shown compactly. Only
                        values that exist appear — a blank chip is worse than no
                        chip, because it implies a reading we don't have. */}
                    {(() => {
                      const f = full && (full.vehicles || []).find((x) => String(x.id) === String(v.id));
                      if (!f) return null;
                      // A truck that stopped reporting months ago is almost
                      // certainly retired. Showing its last-known fuel level
                      // next to a live truck's invites reading stale numbers as
                      // current, so say the age instead of the readings.
                      if (f.stale) {
                        const days = f.ageHours != null ? Math.round(f.ageHours / 24) : null;
                        return (
                          <div style={{ marginTop: '5px', fontSize: '10px', color: MUTED }}>
                            <span style={{
                              fontWeight: 700, color: '#9aa0b4', background: 'rgba(255,255,255,0.05)',
                              border: HAIR, borderRadius: '5px', padding: '1px 6px',
                            }}>
                              no report {days != null ? (days > 60 ? `in ${Math.round(days / 30)} months` : `for ${days}d`) : ''}
                            </span>
                            <span style={{ marginLeft: '6px' }}>likely out of fleet · readings not current</span>
                          </div>
                        );
                      }
                      const chips = [
                        f.fuelPct != null && [`⛽ ${Math.round(f.fuelPct)}%`, f.fuelPct < 20 ? '#f59e0b' : MUTED],
                        f.defPct != null && [`DEF ${f.defPct}%`, f.defPct < 20 ? '#f59e0b' : MUTED],
                        f.odometerMi != null && [`${f.odometerMi.toLocaleString()} mi`, MUTED],
                        f.coolantF != null && [`${f.coolantF}°F`, f.coolantF > 220 ? '#ef4444' : MUTED],
                        // batteryV is already filtered server-side — a reading
                        // under 6V means "gateway not reading it", not a flat
                        // battery, and showing it caused 53 false alarms.
                        f.batteryV != null && [`${f.batteryV}V`, f.batteryV < 12.2 ? '#f59e0b' : MUTED],
                        f.rpm ? [`${Math.round(f.rpm)} rpm`, MUTED] : null,
                        f.ambientF != null && [`air ${f.ambientF}°F`, MUTED],
                        f.faultCount > 0 && [`⚠ ${f.faultCount} fault${f.faultCount > 1 ? 's' : ''}`, '#ef4444'],
                      ].filter(Boolean);
                      if (!chips.length) return null;
                      return (
                        <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {chips.map(([text, tone], k) => (
                            <span key={k} style={{
                              fontSize: '10px', fontWeight: 700, color: tone,
                              background: 'rgba(255,255,255,0.05)', border: HAIR,
                              borderRadius: '5px', padding: '1px 6px',
                            }}>{text}</span>
                          ))}
                          {f.vin ? <span style={{ fontSize: '10px', color: MUTED, padding: '1px 4px' }}>{f.vin}</span> : null}
                        </div>
                      );
                    })()}
                  </div>
                  {/* Driver comes from Samsara's live assignment when we have
                      it — the company already maintains that roster, so asking
                      them to re-key 193 drivers would create a second copy that
                      immediately drifts. Falls back to our own assignment for
                      fleets with no telematics connected. */}
                  {(() => {
                    const sam = tmDrivers && tmDrivers.byVehicle && tmDrivers.byVehicle[String(v.id)];
                    if (sam) {
                      return (
                        <div style={{ minWidth: '170px', textAlign: 'right' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700 }}>{sam.name}</div>
                          <div style={{ fontSize: '11px', color: MUTED }}>
                            {sam.dutyStatus ? sam.dutyStatus.replace(/_/g, ' ') : ''}
                            {sam.phone ? ` · ${sam.phone}` : ''}
                          </div>
                        </div>
                      );
                    }
                    if (tmDrivers) {
                      return <div style={{ minWidth: '170px', textAlign: 'right', fontSize: '12px', color: MUTED }}>unassigned</div>;
                    }
                    return (
                      <select
                        value={data.assignments[String(v.id)] || ''}
                        onChange={(e) => persist(assignDriver(data, v.id, e.target.value))}
                        style={{ ...inp, width: 'auto', minWidth: '150px', padding: '8px 10px', fontSize: '13px' }}
                      >
                        <option value="">— no driver —</option>
                        {data.drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    );
                  })()}
                  <button
                    title="Remove this vehicle from the fleet"
                    onClick={async () => {
                      if (!window.confirm(`Remove ${v.name} from this fleet?`)) return;
                      try { await releaseVehicle(v.id); refresh(); } catch (e) { setErr(e.message); }
                    }}
                    style={{ ...btn(false), padding: '6px 10px', fontSize: '12px', color: MUTED }}
                  >✕</button>
                </div>
              );
            })}
          </div>
        )}

        {!loading && tab === 'drivers' && (
          tmDrivers
            ? <TelematicsDrivers roster={tmDrivers} />
            : <DriversTab data={data} onChange={persist} />
        )}

        <div style={{ textAlign: 'center', color: MUTED, fontSize: '12px', marginTop: '24px' }}>
          {PRODUCT.company} · {PRODUCT.supportPhoneDisplay}
        </div>
      </div>
    </div>
  );
}


// ---------- connect the company's own telematics ----------
// Florida Beauty already runs 170 vehicles on Samsara. Asking them to move
// hardware would be absurd — we read their fleet instead. The token is posted
// straight to our server and never stored in the browser.
function ConnectTelematics({ status, onConnected }) {
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  if (status && status.connected) {
    return (
      <div style={{ ...card, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }} />
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px' }}>
            Samsara connected{status.carrier ? ` — ${status.carrier}` : ''}
          </div>
          <div style={{ fontSize: '12px', color: MUTED }}>
            Vehicles are read live from your own telematics account.
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const r = await connectSamsara(token.trim());
      setMsg({ ok: true, text: `Connected${r.carrier ? ` to ${r.carrier}` : ''}.` });
      setToken('');
      onConnected();
    } catch (ex) { setMsg({ ok: false, text: ex.message }); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} style={{ ...card, marginBottom: '14px' }}>
      <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>Connect your telematics</div>
      <div style={{ fontSize: '12px', color: MUTED, marginBottom: '12px', lineHeight: 1.5 }}>
        Already tracking with Samsara? Paste an API token and your vehicles appear here — no new hardware.
        The token is stored on our server and never in your browser. Read-only scopes are enough.
      </div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <input
          type="password" value={token} onChange={(e) => setToken(e.target.value)}
          placeholder="samsara_api_..." autoComplete="off"
          style={{ ...inp, flex: 1, minWidth: '220px' }}
        />
        <button type="submit" disabled={busy || !token.trim()} style={{ ...btn(true), opacity: busy || !token.trim() ? 0.5 : 1 }}>
          {busy ? 'Checking…' : 'Connect'}
        </button>
      </div>
      {msg && (
        <div style={{
          marginTop: '10px', fontSize: '13px', borderRadius: '10px', padding: '9px 11px',
          background: msg.ok ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)',
          border: `1px solid ${msg.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.32)'}`,
          color: msg.ok ? '#86efac' : '#fca5a5',
        }}>{msg.text}</div>
      )}
    </form>
  );
}

// ---------- add a vehicle by claim code ----------
function AddVehicle({ onAdded }) {
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState(null); // { ok, text }
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const r = await claimVehicle(code);
      // a car that belongs to a customer joins only after its owner approves
      setMsg({ ok: true, text: r.pending ? `Request sent to the owner of ${r.name || 'that vehicle'}. It joins your fleet once they approve.` : `Added ${r.name || 'vehicle'} to your fleet.` });
      setCode('');
      onAdded();
    } catch (ex) { setMsg({ ok: false, text: ex.message }); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} style={{ ...card, marginBottom: '14px' }}>
      <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>Add a vehicle</div>
      <div style={{ fontSize: '12px', color: MUTED, marginBottom: '12px' }}>
        Enter the code from the vehicle&apos;s owner. A vehicle can only be on one fleet at a time.
      </div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. TA-1NBDJQV"
          style={{ ...inp, flex: 1, minWidth: '160px', letterSpacing: '2px', fontWeight: 700 }}
        />
        <button type="submit" disabled={busy || !code.trim()} style={{ ...btn(true), opacity: busy || !code.trim() ? 0.5 : 1 }}>
          {busy ? 'Adding…' : 'Add vehicle'}
        </button>
      </div>
      {msg && (
        <div style={{
          marginTop: '10px', fontSize: '13px', borderRadius: '10px', padding: '9px 11px',
          background: msg.ok ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)',
          border: `1px solid ${msg.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.32)'}`,
          color: msg.ok ? '#86efac' : '#fca5a5',
        }}>{msg.text}</div>
      )}
    </form>
  );
}



// ---------- what this telematics account actually exposes ----------
// Built because guessing API shapes from documentation cost us real time. This
// probes each endpoint live and reports status, so we know what we're working
// with instead of assuming — and so the customer can see exactly which parts of
// their Samsara plan we can and cannot read.
function DataAudit({ probe, onProbe }) {
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); await onProbe(); setBusy(false); };

  return (
    <>
      <div style={{ ...card, marginBottom: '14px' }}>
        <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>What we can read from this account</div>
        <div style={{ fontSize: '12px', color: MUTED, marginBottom: '12px', lineHeight: 1.55 }}>
          Probes every Samsara endpoint once and reports what came back. A 403 means the plan or token
          scope doesn&apos;t allow it — useful to know before promising a feature that depends on it.
        </div>
        <button onClick={run} disabled={busy} style={{ ...btn(true), opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Probing…' : probe ? 'Probe again' : 'Run the probe'}
        </button>
      </div>

      {probe && (
        <>
          <div style={{ ...card, marginBottom: '14px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>
              {probe.summary.available} of {probe.summary.total} endpoints available
            </div>
            {probe.summary.blocked && probe.summary.blocked.length > 0 && (
              <div style={{ fontSize: '12px', color: '#fca5a5', marginTop: '6px' }}>
                Blocked (403): {probe.summary.blocked.join(', ')}
              </div>
            )}
          </div>
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {probe.probes.map((p, i) => (
              <div key={p.key} style={{ padding: '12px 16px', borderTop: i ? HAIR : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                    background: p.available ? '#22c55e' : p.status === 403 ? '#f59e0b' : '#6b7280',
                  }} />
                  <div style={{ fontWeight: 700, fontSize: '13px', flex: 1 }}>{p.label}</div>
                  <span style={{ fontSize: '11px', color: MUTED }}>{p.status || 'error'}</span>
                </div>
                {p.note && <div style={{ fontSize: '11px', color: MUTED, marginLeft: '18px', marginTop: '3px' }}>{p.note}</div>}
                {p.fields && p.fields.length > 0 && (
                  <div style={{ fontSize: '11px', color: MUTED, marginLeft: '18px', marginTop: '5px', lineHeight: 1.6 }}>
                    {p.fields.slice(0, 18).map((f) => (
                      <span key={f} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '5px', padding: '1px 6px', marginRight: '4px', display: 'inline-block', marginBottom: '3px' }}>{f}</span>
                    ))}
                    {p.fields.length > 18 ? <span>+{p.fields.length - 18} more</span> : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ---------- drivers, read from the company's own telematics ----------
// Read-only on purpose. Samsara is the system of record for the roster; a
// second editable copy here would drift within a week and nobody would know
// which was right.
function TelematicsDrivers({ roster }) {
  const [q, setQ] = useState('');
  const c = roster.counts || {};
  const rows = (roster.drivers || []).filter((d) => {
    if (!q.trim()) return true;
    const hay = `${d.name} ${d.username} ${d.vehicle || ''} ${d.phone}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const tone = (s) => (s === 'driving' ? '#22c55e' : s === 'onDuty' ? '#f59e0b' : MUTED);
  return (
    <>
      <div style={{ ...card, marginBottom: '14px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontWeight: 700, fontSize: '15px' }}>
            {c.total} drivers · {c.assigned} in a truck · {c.driving} driving
          </div>
          <div style={{ fontSize: '12px', color: MUTED }}>
            Read live from Samsara — the roster stays where your team already maintains it.
          </div>
        </div>
        <input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search drivers…"
          style={{ ...inp, width: 'auto', minWidth: '200px' }}
        />
      </div>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {rows.length === 0 && (
          <div style={{ padding: '26px', textAlign: 'center', color: MUTED, fontSize: '14px' }}>No drivers match.</div>
        )}
        {rows.map((d, i) => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 16px', borderTop: i ? HAIR : 'none', flexWrap: 'wrap' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: tone(d.dutyStatus), flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: '150px' }}>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>{d.name}</div>
              <div style={{ fontSize: '11px', color: MUTED }}>
                {[d.username && `#${d.username}`, d.phone, d.license && `${d.licenseState || ''} ${d.license}`.trim()]
                  .filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ minWidth: '110px', textAlign: 'right' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: d.vehicle ? TXT : MUTED }}>
                {d.vehicle ? `Truck ${d.vehicle}` : 'no truck'}
              </div>
              <div style={{ fontSize: '11px', color: tone(d.dutyStatus) }}>
                {d.dutyStatus ? d.dutyStatus.replace(/([A-Z])/g, ' $1').toLowerCase() : (d.status || '')}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ---------- drivers ----------
function DriversTab({ data, onChange }) {
  const [f, setF] = useState({ name: '', phone: '', license: '', email: '' });
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const add = (e) => {
    e.preventDefault();
    try { onChange(addDriver(data, f)); setF({ name: '', phone: '', license: '', email: '' }); setErr(''); }
    catch (ex) { setErr(ex.message); }
  };

  return (
    <>
      <form onSubmit={add} style={{ ...card, marginBottom: '14px' }}>
        <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '12px' }}>Add a driver</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '12px' }}>
          <div>
            <label style={{ fontSize: '11px', color: MUTED, fontWeight: 600 }}>Name</label>
            <input style={{ ...inp, marginTop: '4px' }} value={f.name} onChange={set('name')} placeholder="Jane Smith" />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: MUTED, fontWeight: 600 }}>Phone</label>
            <input style={{ ...inp, marginTop: '4px' }} value={f.phone} onChange={set('phone')} placeholder="(305) 555-0100" />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: MUTED, fontWeight: 600 }}>License #</label>
            <input style={{ ...inp, marginTop: '4px' }} value={f.license} onChange={set('license')} />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: MUTED, fontWeight: 600 }}>Email</label>
            <input style={{ ...inp, marginTop: '4px' }} value={f.email} onChange={set('email')} />
          </div>
        </div>
        {err && <div style={{ color: '#fca5a5', fontSize: '13px', marginBottom: '10px' }}>{err}</div>}
        <button type="submit" style={btn(true)}>+ Add driver</button>
      </form>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {data.drivers.length === 0 && (
          <div style={{ padding: '28px', textAlign: 'center', color: MUTED, fontSize: '14px' }}>
            No drivers yet. Add one above, then assign them to a vehicle.
          </div>
        )}
        {data.drivers.map((d, i) => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 16px', borderTop: i ? HAIR : 'none' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>{d.name}</div>
              <div style={{ fontSize: '12px', color: MUTED }}>
                {[d.phone, d.license && `Lic ${d.license}`, d.email].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            <button
              onClick={() => { if (window.confirm(`Remove ${d.name}? This also clears their vehicle assignment.`)) onChange(removeDriver(data, d.id)); }}
              style={{ ...btn(false), padding: '7px 12px', fontSize: '13px', color: '#fca5a5' }}
            >Remove</button>
          </div>
        ))}
      </div>
    </>
  );
}

// ---------- entry ----------
export default function FleetPage() {
  const [me, setMe] = useState(() => getFleetSession());
  if (!me) return <FleetLogin onIn={setMe} />;
  return <FleetDashboard me={me} onOut={() => { fleetLogout(); setMe(null); }} />;
}
